import React, { useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LogOut, Loader2 } from 'lucide-react';
import { portalGet, getPortalToken, clearPortalToken } from '../../lib/portalApi';
import PortalChangePasswordModal from './PortalChangePasswordModal';

export default function PortalLayout() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, account: null, company: null });

  useEffect(() => {
    if (!getPortalToken()) return setState({ loading: false, account: null, company: null });
    portalGet('/portal/me')
      .then((d) => setState({ loading: false, account: d.account, company: d.company }))
      .catch(() => setState({ loading: false, account: null, company: null }));
  }, []);

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading…</div>;
  }
  if (!state.account) {
    return <Navigate to="/portal/login" replace />;
  }

  const logout = () => {
    clearPortalToken();
    navigate('/portal/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/elr-logo-teal.png" alt="ELR Industries" className="h-6 w-auto" />
            <span className="text-slate-300">|</span>
            <span className="text-sm font-display font-bold text-slate-700 truncate max-w-[200px]">{state.company?.name || state.account.company_name}</span>
          </div>
          <button onClick={logout} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="portal-logout">
            <LogOut size={13} /> Sign out
          </button>
        </div>
        <nav className="max-w-5xl mx-auto px-4 sm:px-6 flex gap-1 -mb-px">
          {[
            ['/portal', 'Deliveries', true],
            ['/portal/orders', 'Sales Orders', false],
            ['/portal/request', 'New Request', false],
          ].map(([to, label, end]) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) => `px-4 py-2.5 text-sm font-display font-semibold border-b-2 ${isActive ? 'border-[#1F7A6E] text-[#0F172A]' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
              {label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Outlet context={{ account: state.account, company: state.company }} />
      </main>
      {state.account.must_change_password && (
        <PortalChangePasswordModal onDone={() => setState({ ...state, account: { ...state.account, must_change_password: false } })} />
      )}
    </div>
  );
}
